import React, { useEffect, useState } from 'react';

/** Multiple values must be comma-separated (e.g. #summer, #launch). */
function parseCommaList(text) {
  return (text || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinCommaList(arr) {
  return (arr || []).join(', ');
}

function contentTypeFromBrief(b) {
  const ct = (b?.content_type || '').toLowerCase();
  if (ct === 'reel' || ct === 'reels' || ct === 'video') return 'reel';
  if (ct === 'post') return 'post';
  if (b?.require_reel) return 'reel';
  return '';
}

const MULTI_HINT = 'Comma-separated. Passes if any one item in the list matches.';

const BLOCKLIST_HINT = 'Comma-separated. Any match in the caption → Review suggested.';

const CONTENT_FORMAT_OPTIONS = [
  { id: '', label: 'Any format', desc: 'Skip format check' },
  { id: 'reel', label: 'Reels / video', desc: 'REELS or VIDEO only' },
  { id: 'post', label: 'Feed post', desc: 'Image or carousel' },
];

/**
 * Brand inputs for Post Guard rules (stored on campaign). All fields optional.
 */
export default function PerfHubPostGuardBriefModal({
  open,
  onClose,
  onSave,
  busy,
  campaignName,
  initialBrief,
}) {
  const [brandHandles, setBrandHandles] = useState('');
  const [requiredHashtags, setRequiredHashtags] = useState('');
  const [blocklist, setBlocklist] = useState('');
  const [ctaKeywords, setCtaKeywords] = useState('');
  const [contentType, setContentType] = useState('');

  useEffect(() => {
    if (!open) return;
    const b = initialBrief || {};
    setBrandHandles(joinCommaList(b.brand_handles));
    setRequiredHashtags(
      joinCommaList((b.required_hashtags || []).map((t) => (t.startsWith('#') ? t : `#${t}`))),
    );
    setBlocklist(joinCommaList(b.blocklist_keywords));
    setCtaKeywords(joinCommaList(b.cta_keywords));
    setContentType(contentTypeFromBrief(b));
  }, [open, initialBrief]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      brand_handles: parseCommaList(brandHandles).map((h) => h.replace(/^@/, '')),
      required_hashtags: parseCommaList(requiredHashtags).map((t) => t.replace(/^#/, '')),
      blocklist_keywords: parseCommaList(blocklist),
      cta_keywords: parseCommaList(ctaKeywords),
      content_type: contentType || '',
    });
  };

  return (
    <div className="ph-pg-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ph-pg-modal ph-pg-modal--rules"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ph-pg-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ph-pg-modal-hero">
          <div className="ph-pg-modal-hero-text">
            <span className="ph-pg-modal-hero-icon" aria-hidden>
              🛡️
            </span>
            <div>
              <h3 id="ph-pg-modal-title">Post Guard rules</h3>
              <p className="ph-pg-modal-hero-sub">
                Optional checks on synced Instagram posts. Connection and live post are always
                verified.
              </p>
            </div>
          </div>
          {campaignName && <span className="ph-pg-modal-campaign-pill">{campaignName}</span>}
          <button
            type="button"
            className="ph-pg-modal-close"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
          >
            ×
          </button>
        </header>

        <form className="ph-pg-modal-form" onSubmit={handleSubmit}>
          <div className="ph-pg-modal-tip" role="note">
            <p className="ph-pg-modal-tip-title">How matching works</p>
            <ul className="ph-pg-modal-tip-list">
              <li>
                <strong>Handles, hashtags, CTA</strong> — any one match passes that rule.
              </li>
              <li>
                <strong>Blocked words</strong> — any one match flags Review suggested.
              </li>
              <li>
                <strong>Empty fields</strong> — that rule is skipped.
              </li>
            </ul>
          </div>

          <div className="ph-pg-modal-section">
            <h4 className="ph-pg-modal-section-title">Caption &amp; mentions</h4>
            <div className="ph-pg-modal-fields">
              <div className="ph-pg-modal-field">
                <label className="ph-pg-modal-label" htmlFor="ph-pg-handles">
                  <span className="ph-pg-modal-label-icon" aria-hidden>
                    @
                  </span>
                  Brand @handles
                  <span className="ph-pg-optional">optional</span>
                </label>
                <input
                  id="ph-pg-handles"
                  className="ph-input ph-input-full ph-pg-modal-input"
                  placeholder="@yourbrand, @partner"
                  value={brandHandles}
                  onChange={(e) => setBrandHandles(e.target.value)}
                  disabled={busy}
                />
                <p className="ph-pg-field-hint">{MULTI_HINT}</p>
              </div>

              <div className="ph-pg-modal-field">
                <label className="ph-pg-modal-label" htmlFor="ph-pg-tags">
                  <span className="ph-pg-modal-label-icon" aria-hidden>
                    #
                  </span>
                  Required hashtags
                  <span className="ph-pg-optional">optional</span>
                </label>
                <input
                  id="ph-pg-tags"
                  className="ph-input ph-input-full ph-pg-modal-input"
                  placeholder="#SummerLaunch, #Partner, #nature"
                  value={requiredHashtags}
                  onChange={(e) => setRequiredHashtags(e.target.value)}
                  disabled={busy}
                />
                <p className="ph-pg-field-hint">{MULTI_HINT}</p>
              </div>

              <div className="ph-pg-modal-field">
                <label className="ph-pg-modal-label" htmlFor="ph-pg-cta">
                  <span className="ph-pg-modal-label-icon" aria-hidden>
                    ↗
                  </span>
                  CTA keywords
                  <span className="ph-pg-optional">optional</span>
                </label>
                <input
                  id="ph-pg-cta"
                  className="ph-input ph-input-full ph-pg-modal-input"
                  placeholder="shop now, link in bio, dm"
                  value={ctaKeywords}
                  onChange={(e) => setCtaKeywords(e.target.value)}
                  disabled={busy}
                />
                <p className="ph-pg-field-hint">{MULTI_HINT}</p>
              </div>
            </div>
          </div>

          <div className="ph-pg-modal-section">
            <h4 className="ph-pg-modal-section-title">Format &amp; safety</h4>
            <div className="ph-pg-modal-fields">
              <fieldset className="ph-pg-modal-field ph-pg-format-fieldset">
                <legend className="ph-pg-modal-label">
                  <span className="ph-pg-modal-label-icon" aria-hidden>
                    ▶
                  </span>
                  Type of content
                  <span className="ph-pg-optional">optional</span>
                </legend>
                <div className="ph-pg-format-options" role="group" aria-label="Content format">
                  {CONTENT_FORMAT_OPTIONS.map((opt) => (
                    <button
                      key={opt.id || 'any'}
                      type="button"
                      className={`ph-pg-format-option ${contentType === opt.id ? 'is-active' : ''}`}
                      disabled={busy}
                      aria-pressed={contentType === opt.id}
                      onClick={() => setContentType(opt.id)}
                    >
                      <span className="ph-pg-format-option-label">{opt.label}</span>
                      <span className="ph-pg-format-option-desc">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </fieldset>

              <div className="ph-pg-modal-field">
                <label className="ph-pg-modal-label" htmlFor="ph-pg-blocklist">
                  <span className="ph-pg-modal-label-icon ph-pg-modal-label-icon--warn" aria-hidden>
                    !
                  </span>
                  Blocked words or hashtags
                  <span className="ph-pg-optional">optional</span>
                </label>
                <input
                  id="ph-pg-blocklist"
                  className="ph-input ph-input-full ph-pg-modal-input ph-pg-modal-input--warn"
                  placeholder="competitor, alcohol, #bannedtag"
                  value={blocklist}
                  onChange={(e) => setBlocklist(e.target.value)}
                  disabled={busy}
                />
                <p className="ph-pg-field-hint">{BLOCKLIST_HINT}</p>
              </div>
            </div>
          </div>

          <footer className="ph-pg-modal-footer">
            <button type="button" className="btn-outline" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary ph-btn-glow" disabled={busy}>
              {busy ? 'Saving…' : 'Save rules & refresh'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
